import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

export default function BSACalc() {
  const [tab, setTab] = useState(0);
  const [gender, setGender] = useState('male');
  const [wtLb, setWtLb] = useState(155);
  const [hFt, setHFt] = useState(5);
  const [hIn, setHIn] = useState(9);
  const [wtKg, setWtKg] = useState(70);
  const [hCm, setHCm] = useState(175);
  const clear = () => { setGender('male'); setWtLb(155); setHFt(5); setHIn(9); setWtKg(70); setHCm(175); };

  const c = useMemo(() => {
    const weight = tab === 0 ? (+wtLb || 0) * 0.453592 : +wtKg || 0;
    const height = tab === 0 ? ((+hFt || 0) * 12 + (+hIn || 0)) * 2.54 : +hCm || 0;
    const dubois = 0.007184 * Math.pow(weight, 0.425) * Math.pow(height, 0.725);
    const mosteller = Math.sqrt(weight * height / 3600);
    const haycock = 0.024265 * Math.pow(weight, 0.5378) * Math.pow(height, 0.3964);
    const gehan = 0.0235 * Math.pow(weight, 0.51456) * Math.pow(height, 0.42246);
    const boyd = 0.0003207 * Math.pow(height, 0.3) * Math.pow(weight * 1000, 0.7285 - 0.0188 * Math.log10(weight * 1000));
    const fujimoto = 0.008883 * Math.pow(weight, 0.444) * Math.pow(height, 0.663);
    const schlich = gender === 'male'
      ? 0.000579479 * Math.pow(weight, 0.38) * Math.pow(height, 1.24)
      : 0.000975482 * Math.pow(weight, 0.46) * Math.pow(height, 1.08);
    return { dubois, mosteller, haycock, gehan, boyd, fujimoto, schlich };
  }, [tab, gender, wtLb, hFt, hIn, wtKg, hCm]);

  return (
    <CalcShell look="cnet" title="Body Surface Area Calculator" subtitle="Compute total body surface area (BSA) using several popular formulas.">
      <CalcNetPanel
        slug="bsa"
        activeTab={tab}
        onTabChange={setTab}
        belowGrid={
          <div className="calc-card p-6">
            <h3 className="font-bold text-slate-900 mb-3">Table of Average BSAs</h3>
            <table className="w-full text-sm border-collapse max-w-md">
              <thead><tr>
                <th className="bg-[#1e40af] text-white px-3 py-2 text-left border">Population</th>
                <th className="bg-[#1e40af] text-white px-3 py-2 text-right border">ft²</th>
                <th className="bg-[#1e40af] text-white px-3 py-2 text-right border">m²</th>
              </tr></thead>
              <tbody>
                <tr><td className="border px-3 py-1.5">Newborn child</td><td className="border px-3 py-1.5 text-right">2.69</td><td className="border px-3 py-1.5 text-right">0.25</td></tr>
                <tr className="bg-slate-50"><td className="border px-3 py-1.5">Two-year-old child</td><td className="border px-3 py-1.5 text-right">5.38</td><td className="border px-3 py-1.5 text-right">0.5</td></tr>
                <tr><td className="border px-3 py-1.5">Ten-year-old child</td><td className="border px-3 py-1.5 text-right">12.27</td><td className="border px-3 py-1.5 text-right">1.14</td></tr>
                <tr className="bg-slate-50"><td className="border px-3 py-1.5">Adult female</td><td className="border px-3 py-1.5 text-right">17.22</td><td className="border px-3 py-1.5 text-right">1.6</td></tr>
                <tr><td className="border px-3 py-1.5">Adult male</td><td className="border px-3 py-1.5 text-right">20.45</td><td className="border px-3 py-1.5 text-right">1.9</td></tr>
              </tbody>
            </table>
          </div>
        }
        right={
          <div>
            <div className="text-lg font-bold text-slate-800 mb-2">Body Surface Area Results</div>
            <ResultTable
              testid="bsa-result-table"
              headers={['Formula', 'BSA (m²)', 'BSA (ft²)', 'BSA (in²)']}
              rows={[
                ['Du Bois', c.dubois.toFixed(4), (c.dubois * 10.7639).toFixed(4), (c.dubois * 1550.0031).toFixed(2)],
                ['Mosteller', <span className="font-bold text-[#2f9c3f]">{c.mosteller.toFixed(4)}</span>, <span className="font-bold text-[#2f9c3f]">{(c.mosteller * 10.7639).toFixed(4)}</span>, <span className="font-bold text-[#2f9c3f]">{(c.mosteller * 1550.0031).toFixed(2)}</span>],
                ['Haycock', c.haycock.toFixed(4), (c.haycock * 10.7639).toFixed(4), (c.haycock * 1550.0031).toFixed(2)],
                ['Gehan & George', c.gehan.toFixed(4), (c.gehan * 10.7639).toFixed(4), (c.gehan * 1550.0031).toFixed(2)],
                ['Boyd', c.boyd.toFixed(4), (c.boyd * 10.7639).toFixed(4), (c.boyd * 1550.0031).toFixed(2)],
                ['Fujimoto', c.fujimoto.toFixed(4), (c.fujimoto * 10.7639).toFixed(4), (c.fujimoto * 1550.0031).toFixed(2)],
                ['Schlich', c.schlich.toFixed(4), (c.schlich * 10.7639).toFixed(4), (c.schlich * 1550.0031).toFixed(2)],
              ]}
            />
          </div>
        }
      >
        <Row label="Gender"><RadioGroup name="bsa-g" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]} /></Row>
        {tab === 0 ? <>
          <Row label="Weight"><UnitInput value={wtLb} onChange={setWtLb} unit="pounds" testid="bsa-wlb" /></Row>
          <Row label="Height"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={hFt} inches={hIn} onFt={setHFt} onIn={setHIn} testidPrefix="bsa-h" /></div></Row>
        </> : <>
          <Row label="Weight"><UnitInput value={wtKg} onChange={setWtKg} unit="kg" testid="bsa-wkg" /></Row>
          <Row label="Height"><UnitInput value={hCm} onChange={setHCm} unit="cm" testid="bsa-hcm" /></Row>
        </>}
      </CalcNetPanel>

      <StepsPanel steps={[
        `Mosteller: BSA = √( height (cm) × weight (kg) / 3600 )`,
        `= √( ${(tab===0? ((+hFt||0)*12+(+hIn||0))*2.54 : +hCm||0).toFixed(1)} × ${(tab===0? (+wtLb||0)*0.453592 : +wtKg||0).toFixed(1)} / 3600 ) ≈ <b>${c.mosteller.toFixed(2)} m²</b>`,
      ]} />
    </CalcShell>
  );
}
